import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ReadByActionSince } from 'services/log-cobranca';
import { Read as ReadTitulos } from 'services/titulo';
import { exposeStrapiError } from 'utils';
import {
  SETTLEMENT_TYPES,
  buildSettlementCards,
  buildSettlementLogIndex,
  buildSettlementQuery,
  buildSettlementRows,
  currentMonthRange,
  isValidRange,
  readAllPages,
} from 'utils/billingReports';

// Relatorio de baixas do menu Cobranca. Busca os titulos baixados no periodo
// (`GET /titulos`, a fonte dos valores) e, em paralelo, os logs `baixa` desde
// o inicio do periodo, so para saber quem baixou e por qual origem. O log e
// gravado no dia em que a baixa foi registrada, que e sempre o da
// `data_baixa` ou depois dele (a data nao pode ser futura), por isso basta
// buscar a partir do `from`.
export default function useSettlementReport() {
  const { filterExpression } = useContext(CoreContext);
  const [period, setPeriod] = useState(currentMonthRange);
  const [type, setType] = useState(SETTLEMENT_TYPES.payment);
  const [loading, setLoading] = useState(false);
  const [titulos, setTitulos] = useState([]);
  const [logs, setLogs] = useState([]);

  const { from, to } = period;
  const validRange = isValidRange(period);

  const reload = useCallback(async () => {
    if (!isValidRange({ from, to })) {
      return;
    }
    setLoading(true);
    try {
      // O log tem catch proprio: sem ele o relatorio sai, so sem operador.
      const [items, logItems] = await Promise.all([
        readAllPages(
          (page) => ReadTitulos(buildSettlementQuery({ from, to, type, page })),
          exposeStrapiError
        ),
        readAllPages(
          (page) => ReadByActionSince('baixa', from, page),
          (result) => !result || !!result?.error
        ).catch((err) => {
          console.error('Erro ao carregar os logs de baixa:', err);
          return null;
        }),
      ]);
      setTitulos(items || []);
      setLogs(logItems || []);
    } catch (err) {
      console.error('Erro ao carregar as baixas:', err);
      toast.error('Erro ao carregar as baixas');
    } finally {
      setLoading(false);
    }
  }, [from, to, type]);

  useEffect(() => {
    reload();
  }, [reload]);

  const rows = useMemo(
    () => buildSettlementRows(titulos, buildSettlementLogIndex(logs)),
    [titulos, logs]
  );

  const visibleRows = useMemo(
    () => rows.filter(filterExpression),
    [rows, filterExpression]
  );

  const cards = useMemo(() => buildSettlementCards(visibleRows), [visibleRows]);

  return {
    loading,
    period,
    setPeriod,
    validRange,
    type,
    setType,
    visibleRows,
    cards,
    reload,
  };
}
